import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

export const fetchModels = createAsyncThunk(
  "model/fetchModels",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if (search) params.append("search", search.trim());
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    const filterPayload = FilterBuilder(filters ?? []);

    const body = {
      filters: filterPayload,
    };

    const response: any = await Axios.post(
      `${API_ENDPOINT?.MASTERS?.MODEL?.LIST}?${params.toString()}`,
      body,
    );
    return {
      data: response.data.data || [],
      rowCount: response.data.count || 0,
    };
  },
);

export const fetchModelById = createAsyncThunk(
  "model/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `${API_ENDPOINT?.MASTERS?.MODEL?.DETAIL(id)}`,
      );
      return response.data?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Model by ID",
      );
    }
  },
);

export const fetchModelByDropDown = createAsyncThunk(
  "model/fetchModelByDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.MASTERS?.MODEL?.DROPDOWN}`,
      );
      return response.data;
    } catch (err: any) {
      console.error("API Error:", err);
      return rejectWithValue(
        err.response?.data || "Failed to fetch Model list",
      );
    }
  },
);

export const fetchModelByMakeDropDown = createAsyncThunk(
  "model/fetchByMakeDropDown",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.MASTERS?.MODEL?.DROPDOWN_BY_MAKE(id)}`,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Model list",
      );
    }
  },
);

export const updateModel = createAsyncThunk(
  "model/updateModel",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.put(
        `${API_ENDPOINT?.MASTERS?.MODEL?.UPDATE(data?.id)}`,
        data,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update Model");
    }
  },
);

export const updateStatusInModel = createAsyncThunk(
  "model/updateStatusInModel",
  async (data: any, { rejectWithValue }) => {
    try {
      const payload = { is_active: data.is_active };
      const response: any = await Axios.patch(
        `${API_ENDPOINT?.MASTERS?.MODEL?.STATUS_UPDATE(data?.model_id)}`,
        payload,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update status in Model",
      );
    }
  },
);

export const addModel = createAsyncThunk(
  "model/addModel",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        `${API_ENDPOINT?.MASTERS?.MODEL?.CREATE}`,
        data,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to add Model");
    }
  },
);

export const deleteModel = createAsyncThunk(
  "model/delete",
  async (model_id: string, { rejectWithValue }) => {
    try {
      await Axios.delete(`${API_ENDPOINT?.MASTERS?.MODEL?.DELETE(model_id)}`);
      return model_id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const exportModelCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: {
    filterField: string;
    filterValue: any;
  }[];
}) => {
  const params = new URLSearchParams();

  if (search) params.append("search", search);

  // -----------------------------
  // Build structured filters body
  // -----------------------------
  const filterPayload = FilterBuilder(filters ?? []);

  const body = {
    filters: filterPayload,
  };

  const response = await Axios.post(
    `${API_ENDPOINT?.MASTERS?.MODEL?.EXPORT}?${params.toString()}`,
    body,
    { responseType: "blob" },
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") ||
      `models_${new Date().toISOString().split("T")[0]}.csv`,
  };
};
